import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import {
  Plus,
  Eye,
  Star,
  Trash2,
  UserPlus,
  ArrowRightLeft,
  History,
  Phone,
  Mail,
  Users,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  Clock,
  User,
  Calendar,
  Smile,
  Meh,
  Frown,
  Tag,
  MapPin,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Minimize2
} from "lucide-react";
import AddNoteDialog, { type NotePayload } from "../components/AddNoteDialog";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AddressFields from "@/components/AddressFields";
import FullScreenLoader from "@/components/FullScreenLoader";
import { crmService } from "../crmApi";
import { useAuth } from "@/contexts/AuthContext";

interface AdditionalContact {
  id: string;
  name: string;
  phone: string;
  type: "secondary" | "other";
  role: string;
  email?: string;
  phoneType: "mobile" | "landline";
  source?: string;
}

interface AdditionalAddress {
  id: string;
  address_line1: string;
  address_line2: string;
  address_line3: string;
  address_landmark: string;
  address_pincode: string;
  address_lat?: string;
  address_lng?: string;
  type: "primary" | "secondary" | "other";
  category: string;
}

interface Note {
  id: number | string;
  date: string;
  author: string;
  text: string;
  category: "call" | "email" | "meeting" | "internal" | "whatsapp" | "other";
  sentiment: "positive" | "neutral" | "negative";
  priority?: "low" | "medium" | "high" | "urgent";
  status: "completed" | "scheduled" | "postponed";
  nextMeeting?: string;
  nextMeetingTime?: string;
  contactName?: string;
}

const leadSchema = z.object({
  id: z.string().optional(),
  bapName: z.string().min(1, "BAP Name is required"),
  tradeName: z.string().min(1, "Trade / Shop Name is required"),
  registeredName: z.string().optional(),
  status: z.string().min(1, "Status is required"),
  role: z.string().min(1, "Role is required"),
  contactName: z.string().min(1, "Contact Name is required"),
  contactType: z.string().min(1, "Contact Type is required"),
  contactPhoneType: z.string().min(1, "Phone Type is required"),
  contactPhone: z.string().min(1, "Contact Phone is required"),
  contactEmail: z.string().email("Invalid email").optional().or(z.literal("")),
  contactLeadSource: z.string().optional(),
  contactOtherLeadSource: z.string().optional(),
  
  // Primary Address Fields (Matching AddressFields standard)
  primary_address_line1: z.string().min(1, "Flat / House no / Building name is required"),
  primary_address_line2: z.string().optional(),
  primary_address_line3: z.string().min(1, "City / Town is required"),
  primary_address_landmark: z.string().optional(),
  primary_address_pincode: z.string().min(1, "Pincode is required"),
  primary_address_lat: z.string().optional(),
  primary_address_lng: z.string().optional(),
  addressCategory: z.string().optional(),
  addressType: z.string().optional(),

  assignedTo: z.string().optional(),
  leadType: z.string().min(1, "Lead Type is required"),
  program_name: z.string().optional(),
  website: z.string().regex(/^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(\/.*)?$/i, "Invalid website URL").optional().or(z.literal("")),
  associatedSalesId: z.string().optional(),
});

type LeadFormValues = z.infer<typeof leadSchema>;

interface CRMLeadFormProps {
  displayMode?: "single-page" | "diary-mode";
  leadId?: string;
}

const CRMLeadForm = ({ displayMode = "single-page", leadId: propLeadId }: CRMLeadFormProps) => {
  const { leadId: routeLeadId } = useParams<{ leadId?: string }>();
  const leadId = propLeadId ?? routeLeadId;
  const { toast } = useToast();
  const { user: currentUser } = useAuth() || { user: null };
  const [currentStep, setCurrentStep] = React.useState(1);
  const totalSteps = 5;

  // Additional Contact Management State
  const [additionalContacts, setAdditionalContacts] = React.useState<AdditionalContact[]>([]);
  const [additionalAddresses, setAdditionalAddresses] = React.useState<AdditionalAddress[]>([]);
  const [notes, setNotes] = React.useState<Note[]>([]);

  const [isAddContactOpen, setIsAddContactOpen] = React.useState(false);
  const [isViewContactsOpen, setIsViewContactsOpen] = React.useState(false);
  const [isAddAddressOpen, setIsAddAddressOpen] = React.useState(false);
  const [isViewAddressesOpen, setIsViewAddressesOpen] = React.useState(false);
  const [isViewNotesOpen, setIsViewNotesOpen] = React.useState(false);
  const [isAddNoteOpen, setIsAddNoteOpen] = React.useState(false);
  const [addNoteCategory, setAddNoteCategory] = React.useState<"call" | "whatsapp" | "email" | "meeting" | "internal" | "other">("internal");
  const [isSwapConfirmOpen, setIsSwapConfirmOpen] = React.useState(false);
  const [isAddressSwapConfirmOpen, setIsAddressSwapConfirmOpen] = React.useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);
  const [contactToDelete, setContactToDelete] = React.useState<AdditionalContact | null>(null);
  const [addressToDelete, setAddressToDelete] = React.useState<AdditionalAddress | null>(null);
  const [isAddressDeleteConfirmOpen, setIsAddressDeleteConfirmOpen] = React.useState(false);

  const [expandedSections, setExpandedSections] = React.useState<Record<number, boolean>>({
    1: true,
    2: true,
    3: true,
    4: true,
    5: true,
  });

  const toggleSection = (section: number) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleAllSections = (expand: boolean) => {
    setExpandedSections({
      1: expand,
      2: expand,
      3: expand,
      4: expand,
      5: expand,
    });
  };

  const allExpanded = Object.values(expandedSections).every(Boolean);
  
  // Configurable statuses, programs and address categories
  const [availableStatuses, setAvailableStatuses] = React.useState<{id: string, name: string}[]>([]);
  const [availablePrograms, setAvailablePrograms] = React.useState<{id: string, name: string}[]>([]);
  const [availableAddressCategories, setAvailableAddressCategories] = React.useState<{id: string, name: string}[]>([]);
  const [availableLeadSources, setAvailableLeadSources] = React.useState<{id: string, name: string}[]>([]);
  const [availableBaps, setAvailableBaps] = React.useState<{id: string, name: string}[]>([]);
  const [availableUsers, setAvailableUsers] = React.useState<any[]>([]);
  const [isDropdownOptionsLoading, setIsDropdownOptionsLoading] = React.useState(true);

  const [contactToMakePrimary, setContactToMakePrimary] = React.useState<AdditionalContact | null>(null);
  const [addressToMakePrimary, setAddressToMakePrimary] = React.useState<AdditionalAddress | null>(null);
  const [editingContactId, setEditingContactId] = React.useState<string | null>(null);
  const [editingAddressId, setEditingAddressId] = React.useState<string | null>(null);
  const [downgradeType, setDowngradeType] = React.useState<"secondary" | "other">("secondary");
  const [addressDowngradeType, setAddressDowngradeType] = React.useState<"secondary" | "other">("secondary");

  
  // New contact form state (simple)
  const [newContact, setNewContact] = React.useState<Partial<AdditionalContact>>({
    name: "",
    phone: "",
    type: "",
    role: "",
    email: "",
    phoneType: "",
    source: ""
  });

  const [newAddress, setNewAddress] = React.useState<Partial<AdditionalAddress>>({
    address_line1: "",
    address_line2: "",
    address_line3: "",
    address_landmark: "",
    address_pincode: "",
    address_lat: "",
    address_lng: "",
    type: "",
    category: ""
  });

  const [otherSource, setOtherSource] = React.useState("");

  const form = useForm<LeadFormValues>({
    resolver: zodResolver(leadSchema),
    defaultValues: {
      id: "",
      tradeName: "",
      registeredName: "",
      bapName: "",
      status: "",
      role: "",
      contactName: "",
      contactType: "primary",
      contactPhoneType: "",
      contactPhone: "",
      contactEmail: "",
      contactLeadSource: "",
      contactOtherLeadSource: "",
      leadType: "",
      program_name: "",
      website: "",
      primary_address_line1: "",
      primary_address_line2: "",
      primary_address_line3: "",
      primary_address_landmark: "",
      primary_address_pincode: "",
      primary_address_lat: "",
      primary_address_lng: "",
      addressCategory: "",
      addressType: "primary",
      assignedTo: "",
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    getValues,
    setValue,
    control,
    watch,
  } = form;

  const isEditing = Boolean(leadId);

  const { data: leadData, isLoading: isLeadLoading } = useQuery<any | null>({
    queryKey: ["crm-lead", leadId],
    queryFn: async () => {
      if (!leadId) {
        return null;
      }
      const response = await crmService.getLead(leadId);
      return response.data;
    },
    enabled: !!leadId,
    staleTime: 60_000,
  });

  React.useEffect(() => {
    if (!leadData) return;

    const mapLookupValue = (value: any, displayValue: any, options: {id: string | number, name: string}[]) => {
      // 1. If we have a display value from backend, use it
      if (displayValue && typeof displayValue === 'string') return displayValue;
      
      // 2. If value is an object, try to get name
      if (value && typeof value === 'object' && value.name) return value.name;
      
      // 3. If value is "Other" (special case for lead source), return "Other"
      if (value === "Other") return "Other";
      
      // 4. If value is a string or number, try to find it in options
      if (value != null && value !== "") {
        const stringVal = String(value);
        // Check if stringVal is already a name in options
        const matchByName = options.find(opt => opt.name === stringVal || (opt.name && opt.name.toLowerCase() === stringVal.toLowerCase()));
        if (matchByName) return matchByName.name;
        
        // Check if it's an ID
        const matchById = options.find(opt => String(opt.id) === stringVal);
        if (matchById) return matchById.name;
        
        return stringVal;
      }
      
      return "";
    };

    reset({
      id: leadData.id != null ? String(leadData.id) : "",
      tradeName: leadData.trade_name ?? "",
      registeredName: leadData.registered_name ?? "",
      bapName: mapLookupValue(leadData.bap_name, leadData.bap_name_display, availableBaps),
      status: mapLookupValue(leadData.status, leadData.status_name, availableStatuses),
      role: leadData.role ?? "",
      contactName: leadData.contact_name ?? "",
      contactType: leadData.contact_type ?? "primary",
      contactPhoneType: leadData.contact_phone_type ?? "",
      contactPhone: leadData.contact_phone ?? "",
      contactEmail: leadData.contact_email ?? "",
      contactLeadSource: leadData.contact_lead_source === "Other" ? "Other" : mapLookupValue(leadData.contact_lead_source, leadData.lead_source_name, availableLeadSources),
      contactOtherLeadSource: leadData.contact_other_lead_source ?? "",
      primary_address_line1: leadData.primary_address_line1 ?? "",
      primary_address_line2: leadData.primary_address_line2 ?? "",
      primary_address_line3: leadData.primary_address_line3 ?? "",
      primary_address_landmark: leadData.primary_address_landmark ?? "",
      primary_address_pincode: leadData.primary_address_pincode ?? "",
      primary_address_lat: leadData.primary_address_lat ?? "",
      primary_address_lng: leadData.primary_address_lng ?? "",
      addressCategory: mapLookupValue(leadData.address_category, leadData.address_category_name, availableAddressCategories),
      addressType: leadData.address_type ?? "primary",
      assignedTo: leadData.assigned_to != null ? String(leadData.assigned_to) : "",
      leadType: leadData.lead_type ?? "",
      program_name: mapLookupValue(leadData.program_name, leadData.program_name_display, availablePrograms),
      website: leadData.website ?? "",
      associatedSalesId: leadData.associated_sales_id ?? "",
    });

    setAdditionalContacts(leadData.additional_contacts ?? []);
    setAdditionalAddresses(leadData.additional_addresses ?? []);
    setNotes((leadData.notes ?? []).map((note: any) => ({
      id: note.id ?? Date.now(),
      date: note.date ?? new Date().toLocaleString(),
      author: note.author ?? "Unknown",
      text: note.text ?? "",
      category: note.category ?? "other",
      sentiment: note.sentiment ?? "neutral",
      status: note.status ?? "completed",
      priority: note.priority ?? undefined,
      nextMeeting: note.next_meeting ?? undefined,
      nextMeetingTime: note.next_meeting_time ?? undefined,
      contactName: note.contact_name ?? undefined,
    })));
  }, [leadData, reset, availableBaps, availableStatuses, availableLeadSources, availablePrograms, availableAddressCategories, availableUsers]);

  React.useEffect(() => {
    const fetchDropdownOptions = async () => {
      setIsDropdownOptionsLoading(true);
      try {
        const [statusRes, programRes, leadSourceRes, addressCategoryRes, bapRes, usersRes] = await Promise.all([
            crmService.getStatuses(),
            crmService.getPrograms(),
            crmService.getLeadSources(),
            crmService.getAddressCategories(),
            crmService.getBaps(),
            crmService.getOrgUsers(),
          ]);

          setAvailableStatuses(statusRes.data || []);
          setAvailablePrograms(programRes.data || []);
          setAvailableLeadSources(leadSourceRes.data || []);
          setAvailableAddressCategories(addressCategoryRes.data || []);
          setAvailableBaps(bapRes.data || []);

          // Use only org-scoped users for assignment
          const orgUsers = usersRes.data || [];
          setAvailableUsers(orgUsers);

      } catch (error) {
        console.error("Error loading dropdown options:", error);
        toast({
          title: "Unable to load CRM dropdowns",
          description: "Please refresh or visit CRM settings to configure values.",
          variant: "destructive",
        });
      } finally {
        setIsDropdownOptionsLoading(false);
      }
    };

    fetchDropdownOptions();
  }, [toast]);

  // Separate effect: default "Assigned To" to the current user for new leads.
  // Runs whenever availableUsers or currentUser changes so it works even if
  // currentUser was null (still bootstrapping) when the dropdown fetch ran.
  React.useEffect(() => {
    if (leadId) return;           // edit mode — don't override the loaded value
    if (!currentUser) return;     // auth not ready yet
    if (availableUsers.length === 0) return; // users not loaded yet

    const selfInOrg = availableUsers.find(
      (u: any) => String(u.id) === String(currentUser.id) || u.username === currentUser.username
    );
    if (selfInOrg) {
      setValue("assignedTo", String(selfInOrg.id));
    }
  }, [availableUsers, currentUser, leadId, setValue]);

  const isPageLoading = isDropdownOptionsLoading || (isEditing && isLeadLoading);

  const handleAddNote = (payload: NotePayload) => {
    const noteToAdd: Note = {
      id: Date.now(),
      date: new Date().toLocaleString(),
      author: currentUser?.username ?? "Agent",
      text: payload.text,
      category: payload.category,
      sentiment: payload.sentiment,
      priority: payload.priority || undefined,
      status: payload.status,
      nextMeeting: payload.next_meeting ?? undefined,
      nextMeetingTime: payload.next_meeting_time ?? undefined,
      contactName: payload.contact_name ?? undefined,
    };
    setNotes((prev) => [noteToAdd, ...prev]);
    toast({
      title: "Note Added",
      description: "Interaction log will be saved with the lead.",
    });
  };

  const handleAddContact = () => {
    if (!newContact.name || !newContact.phone) {
      toast({
        title: "Error",
        description: "Please fill in Name and Phone number",
        variant: "destructive"
      });
      return;
    }
    const contactToAdd: AdditionalContact = {
      id: Math.random().toString(36).substr(2, 9),
      name: newContact.name!,
      phone: newContact.phone!,
      type: (newContact.type as "secondary" | "other") || "secondary",
      role: newContact.role || "Staff",
      email: newContact.email,
      phoneType: (newContact.phoneType as "mobile" | "landline") || "mobile",
      source: newContact.source === "Other" ? otherSource : newContact.source
    };

    const getChanges = (old: any, updated: any) => {
      const changes: string[] = [];
      if (old.name !== updated.name) changes.push(`Name: ${old.name} → ${updated.name}`);
      if (old.role !== updated.role) changes.push(`Role: ${old.role} → ${updated.role}`);
      if (old.phone !== updated.phone) changes.push(`Phone: ${old.phone} → ${updated.phone}`);
      if (old.email !== updated.email) changes.push(`Email: ${old.email || "N/A"} → ${updated.email || "N/A"}`);
      if (old.phoneType !== updated.phoneType) changes.push(`Phone Type: ${old.phoneType} → ${updated.phoneType}`);
      if (old.type && old.type !== updated.type) changes.push(`Type: ${old.type} → ${updated.type}`);
      if (updated.source && old.source !== updated.source) changes.push(`Source: ${old.source || "N/A"} → ${updated.source}`);
      return changes;
    };

    if (editingContactId === "primary") {
      const oldPrimary = {
        name: getValues("contactName"),
        role: getValues("role"),
        phone: getValues("contactPhone"),
        email: getValues("email"),
        phoneType: getValues("mobile") ? "mobile" : "landline"
      };
      
      const changes = getChanges(oldPrimary, contactToAdd);
      const changesText = changes.length > 0 ? ` Changes: ${changes.join(", ")}` : "";

      // Edit Primary Contact logic
      setValue("contactName", contactToAdd.name);
      setValue("role", contactToAdd.role);
      setValue("contactPhone", contactToAdd.phone);
      setValue("email", contactToAdd.email || "");
      
      if (contactToAdd.phoneType === "mobile") {
        setValue("mobile", contactToAdd.phone);
        setValue("landline", "");
      } else {
        setValue("landline", contactToAdd.phone);
        setValue("mobile", "");
      }

      const newNote: Note = {
        id: Date.now(),
        date: new Date().toLocaleString(),
        author: "System",
        text: `Primary contact details for "${contactToAdd.name}" were updated.${changesText}`,
        category: "internal",
        sentiment: "neutral",
        status: "completed"
      };
      setNotes([newNote, ...notes]);

      toast({
        title: "Primary Contact Updated",
        description: `Details for ${contactToAdd.name} have been updated in the form.`
      });
    } else if (editingContactId) {
      // Edit Additional Contact logic
      const oldContact = additionalContacts.find(c => c.id === editingContactId);
      const changes = oldContact ? getChanges(oldContact, contactToAdd) : [];
      const changesText = changes.length > 0 ? ` Changes: ${changes.join(", ")}` : "";

      const updatedContacts = additionalContacts.map(c => 
        c.id === editingContactId ? { ...contactToAdd, id: editingContactId } : c
      );
      setAdditionalContacts(updatedContacts);

      const newNote: Note = {
        id: Date.now(),
        date: new Date().toLocaleString(),
        author: "System",
        text: `Additional contact "${contactToAdd.name}" was updated.${changesText}`,
        category: "internal",
        sentiment: "neutral",
        status: "completed"
      };
      setNotes([newNote, ...notes]);

      toast({
        title: "Contact Updated",
        description: `${contactToAdd.name}'s details have been updated.`
      });
    } else {
      // Add New Contact logic
      setAdditionalContacts([...additionalContacts, contactToAdd]);
      
      const newNote: Note = {
        id: Date.now(),
        date: new Date().toLocaleString(),
        author: "System",
        text: `New additional contact "${contactToAdd.name}" (${contactToAdd.type}) added.`,
        category: "internal",
        sentiment: "neutral",
        status: "completed"
      };
      setNotes([newNote, ...notes]);

      toast({
        title: "Contact added",
        description: `${contactToAdd.name} has been added as a ${contactToAdd.type} contact.`
      });
    }

    setNewContact({ name: "", phone: "", type: "", role: "", email: "", phoneType: "", source: "" });
    setOtherSource("");
    setEditingContactId(null);
    setIsAddContactOpen(false);
  };

  const handleRemoveContact = (id: string) => {
    const contact = additionalContacts.find(c => c.id === id);
    if (contact) {
      setContactToDelete(contact);
      setIsDeleteConfirmOpen(true);
    }
  };

  const confirmDeleteContact = () => {
    if (!contactToDelete) return;
    
    setAdditionalContacts(additionalContacts.filter(c => c.id !== contactToDelete.id));
    
    const newNote: Note = {
      id: Date.now(),
      date: new Date().toLocaleString(),
      author: "System",
      text: `Additional contact "${contactToDelete.name}" (${contactToDelete.phone}) was deleted.`,
      category: "internal",
      sentiment: "neutral",
      status: "completed"
    };
    setNotes([newNote, ...notes]);
    
    setIsDeleteConfirmOpen(false);
    setContactToDelete(null);
    
    toast({
      title: "Contact removed",
      description: `"${contactToDelete.name}" has been deleted and history entry created.`
    });
  };

  const initiateMakePrimary = (contact: AdditionalContact) => {
    setContactToMakePrimary(contact);
    setIsSwapConfirmOpen(true);
  };

  const confirmSwapPrimary = () => {
    if (!contactToMakePrimary) return;

    // Current primary data from form
    const currentPrimary = {
      name: getValues("contactName"),
      role: getValues("role"),
      phone: getValues("contactPhone"),
      email: getValues("email"),
      landline: getValues("landline"),
      mobile: getValues("mobile"),
    };

    // 1. Swap Contact B into Primary (Section 3)
    setValue("contactName", contactToMakePrimary.name);
    setValue("role", contactToMakePrimary.role);
    setValue("contactPhone", contactToMakePrimary.phone);
    setValue("contactEmail", contactToMakePrimary.email || "");
    setValue("contactLeadSource", contactToMakePrimary.source || "");
    setValue("contactType", "primary");
    
    if (contactToMakePrimary.phoneType === "mobile") {
      setValue("mobile", contactToMakePrimary.phone);
      setValue("landline", "");
    } else {
      setValue("landline", contactToMakePrimary.phone);
      setValue("mobile", "");
    }

    // 2. Add old Primary to additional contacts
    const downgradedOldPrimary: AdditionalContact = {
      id: Math.random().toString(36).substr(2, 9),
      name: currentPrimary.name,
      role: currentPrimary.role,
      phone: currentPrimary.phone,
      type: downgradeType,
      email: currentPrimary.email,
      phoneType: currentPrimary.mobile ? "mobile" : "landline"
    };

    const updatedAdditional = additionalContacts.filter(c => c.id !== contactToMakePrimary.id);
    setAdditionalContacts([...updatedAdditional, downgradedOldPrimary]);

    const newNote: Note = {
      id: Date.now(),
      date: new Date().toLocaleString(),
      author: "System",
      text: `Primary contact changed from "${currentPrimary.name}" (${currentPrimary.phone}) to "${contactToMakePrimary.name}" (${contactToMakePrimary.phone}). Old primary moved to ${downgradeType} contacts.`,
      category: "internal",
      sentiment: "neutral",
      status: "completed"
    };
    setNotes([newNote, ...notes]);

    setIsSwapConfirmOpen(false);
    setContactToMakePrimary(null);
    setIsViewContactsOpen(false);

    toast({
      title: "Primary Contact Updated",
      description: `${contactToMakePrimary.name} is now the primary contact. ${currentPrimary.name} moved to additional contacts.`
    });
  };

  const handleAddAddress = () => {
    if (!newAddress.address_line1 || !newAddress.address_pincode) {
      toast({
        title: "Error",
        description: "Please fill in Address Line 1 and Pincode",
        variant: "destructive"
      });
      return;
    }
    const addressToAdd: AdditionalAddress = {
      id: editingAddressId || Math.random().toString(36).substr(2, 9),
      address_line1: newAddress.address_line1!,
      address_line2: newAddress.address_line2 || "",
      address_line3: newAddress.address_line3 || "",
      address_landmark: newAddress.address_landmark || "",
      address_pincode: newAddress.address_pincode!,
      address_lat: newAddress.address_lat || "",
      address_lng: newAddress.address_lng || "",
      type: (newAddress.type as "primary" | "secondary" | "other") || "secondary",
      category: newAddress.category || "Office"
    };

    const getAddressChanges = (old: any, updated: any) => {
      const changes: string[] = [];
      if (old.address_line1 !== updated.address_line1) changes.push(`Address Line 1: ${old.address_line1} → ${updated.address_line1}`);
      if (old.address_line2 !== updated.address_line2) changes.push(`Address Line 2: ${old.address_line2} → ${updated.address_line2}`);
      if (old.address_pincode !== updated.address_pincode) changes.push(`Pincode: ${old.address_pincode} → ${updated.address_pincode}`);
      if (old.address_landmark !== updated.address_landmark) changes.push(`Landmark: ${old.address_landmark || "N/A"} → ${updated.address_landmark || "N/A"}`);
      if (old.address_line3 !== updated.address_line3) changes.push(`City: ${old.address_line3 || "N/A"} → ${updated.address_line3 || "N/A"}`);
      if (old.address_lat !== updated.address_lat) changes.push(`Latitude: ${old.address_lat || "N/A"} → ${updated.address_lat || "N/A"}`);
      if (old.address_lng !== updated.address_lng) changes.push(`Longitude: ${old.address_lng || "N/A"} → ${updated.address_lng || "N/A"}`);
      if (old.type !== updated.type) changes.push(`Type: ${old.type} → ${updated.type}`);
      if (old.category !== updated.category) changes.push(`Category: ${old.category} → ${updated.category}`);
      return changes;
    };

    if (editingAddressId === "primary") {
      const oldPrimary = {
        address_line1: getValues("primary_address_line1"),
        address_line2: getValues("primary_address_line2"),
        address_line3: getValues("primary_address_line3"),
        address_landmark: getValues("primary_address_landmark"),
        address_pincode: getValues("primary_address_pincode"),
        address_lat: getValues("primary_address_lat"),
        address_lng: getValues("primary_address_lng"),
        addressCategory: getValues("addressCategory"),
        type: "primary"
      };
      
      const changes = getAddressChanges(oldPrimary, addressToAdd);
      const changesText = changes.length > 0 ? ` Changes: ${changes.join(", ")}` : "";

      setValue("primary_address_line1", addressToAdd.address_line1);
      setValue("primary_address_line2", addressToAdd.address_line2);
      setValue("primary_address_line3", addressToAdd.address_line3);
      setValue("primary_address_landmark", addressToAdd.address_landmark);
      setValue("primary_address_pincode", addressToAdd.address_pincode);
      setValue("primary_address_lat", addressToAdd.address_lat || "");
      setValue("primary_address_lng", addressToAdd.address_lng || "");
      setValue("addressCategory", addressToAdd.category);

      const newNote: Note = {
        id: Date.now(),
        date: new Date().toLocaleString(),
        author: "System",
        text: `Primary address details updated.${changesText}`,
        category: "internal",
        sentiment: "neutral",
        status: "completed"
      };
      setNotes([newNote, ...notes]);

      toast({
        title: "Primary Address Updated",
        description: "Primary location details have been updated in the form."
      });
    } else if (editingAddressId) {
      const oldAddress = additionalAddresses.find(a => a.id === editingAddressId);
      const changes = oldAddress ? getAddressChanges(oldAddress, addressToAdd) : [];
      const changesText = changes.length > 0 ? ` Changes: ${changes.join(", ")}` : "";

      const updatedAddresses = additionalAddresses.map(a => 
        a.id === editingAddressId ? { ...addressToAdd, id: editingAddressId } : a
      );
      setAdditionalAddresses(updatedAddresses);

      const newNote: Note = {
        id: Date.now(),
        date: new Date().toLocaleString(),
        author: "System",
        text: `Additional address updated.${changesText}`,
        category: "internal",
        sentiment: "neutral",
        status: "completed"
      };
      setNotes([newNote, ...notes]);

      toast({
        title: "Address Updated",
        description: "Address details have been updated."
      });
    } else {
      setAdditionalAddresses([...additionalAddresses, addressToAdd]);
      
      const newNote: Note = {
        id: Date.now(),
        date: new Date().toLocaleString(),
        author: "System",
        text: `New additional address (${addressToAdd.type}) added.`,
        category: "internal",
        sentiment: "neutral",
        status: "completed"
      };
      setNotes([newNote, ...notes]);

      toast({
        title: "Address added",
        description: `New ${addressToAdd.type} address has been added.`
      });
    }

    setNewAddress({ address_line1: "", address_line2: "", address_line3: "", address_landmark: "", address_pincode: "", address_lat: "", address_lng: "", type: "", category: "" });
    setEditingAddressId(null);
    setIsAddAddressOpen(false);
  };

  const handleRemoveAddress = (id: string) => {
    const address = additionalAddresses.find(a => a.id === id);
    if (address) {
      setAddressToDelete(address);
      setIsAddressDeleteConfirmOpen(true);
    }
  };

  const confirmDeleteAddress = () => {
    if (!addressToDelete) return;
    
    setAdditionalAddresses(additionalAddresses.filter(a => a.id !== addressToDelete.id));
    
    const newNote: Note = {
      id: Date.now(),
      date: new Date().toLocaleString(),
      author: "System",
      text: `Additional address (${addressToDelete.type}) was deleted.`,
      category: "internal",
      sentiment: "neutral",
      status: "completed"
    };
    setNotes([newNote, ...notes]);
    
    setIsAddressDeleteConfirmOpen(false);
    setAddressToDelete(null);
    
    toast({
      title: "Address removed",
      description: "Additional address has been deleted."
    });
  };

  const initiateMakePrimaryAddress = (address: AdditionalAddress) => {
    setAddressToMakePrimary(address);
    setIsAddressSwapConfirmOpen(true);
  };

  const confirmAddressSwap = () => {
    if (!addressToMakePrimary) return;

    const currentPrimary = {
      address_line1: getValues("primary_address_line1"),
      address_line2: getValues("primary_address_line2"),
      address_line3: getValues("primary_address_line3"),
      address_landmark: getValues("primary_address_landmark"),
      address_pincode: getValues("primary_address_pincode"),
      address_lat: getValues("primary_address_lat"),
      address_lng: getValues("primary_address_lng"),
      addressCategory: getValues("addressCategory"),
    };

    setValue("primary_address_line1", addressToMakePrimary.address_line1);
    setValue("primary_address_line2", addressToMakePrimary.address_line2);
    setValue("primary_address_line3", addressToMakePrimary.address_line3);
    setValue("primary_address_landmark", addressToMakePrimary.address_landmark);
    setValue("primary_address_pincode", addressToMakePrimary.address_pincode);
    setValue("primary_address_lat", addressToMakePrimary.address_lat || "");
    setValue("primary_address_lng", addressToMakePrimary.address_lng || "");
    setValue("addressCategory", addressToMakePrimary.category);

    const downgradedOldPrimary: AdditionalAddress = {
      id: Math.random().toString(36).substr(2, 9),
      address_line1: currentPrimary.address_line1,
      address_line2: currentPrimary.address_line2 || "",
      address_line3: currentPrimary.address_line3 || "",
      address_landmark: currentPrimary.address_landmark || "",
      address_pincode: currentPrimary.address_pincode,
      address_lat: currentPrimary.address_lat || "",
      address_lng: currentPrimary.address_lng || "",
      category: currentPrimary.addressCategory || "office",
      type: addressDowngradeType
    };

    const updatedAdditional = additionalAddresses.filter(a => a.id !== addressToMakePrimary.id);
    setAdditionalAddresses([...updatedAdditional, downgradedOldPrimary]);

    const newNote: Note = {
      id: Date.now(),
      date: new Date().toLocaleString(),
      author: "System",
      text: `Primary address changed. Old primary moved to ${addressDowngradeType} addresses.`,
      category: "internal",
      sentiment: "neutral",
      status: "completed"
    };
    setNotes([newNote, ...notes]);

    setAddressToMakePrimary(null);
    setIsViewAddressesOpen(false);
    setIsAddressSwapConfirmOpen(false);

    toast({
      title: "Primary Address Updated",
      description: "Primary location updated and old address moved to additional addresses."
    });
  };

  const onSubmit = async (data: LeadFormValues) => {
    // Explicitly validate step 5 fields before final submission in Diary Mode
    if (isDiaryMode) {
      const step5Fields: (keyof LeadFormValues)[] = ["status", "program_name", "leadType", "assignedTo", "associatedSalesId"];
      const isStep5Valid = await form.trigger(step5Fields);
      if (!isStep5Valid) {
        toast({
          title: "Validation Error",
          description: "Please fill in all required fields on the final step.",
          variant: "destructive"
        });
        return;
      }
    }

    const fullData = { 
      bap_name: data.bapName || null,
      trade_name: data.tradeName,
      registered_name: data.registeredName || null,
      status: data.status,
      role: data.role,
      contact_name: data.contactName,
      contact_type: data.contactType,
      contact_phone_type: data.contactPhoneType,
      contact_phone: data.contactPhone,
      contact_email: data.contactEmail || null,
      contact_lead_source: data.contactLeadSource === "Other" ? null : data.contactLeadSource || null,
      contact_other_lead_source: data.contactOtherLeadSource || null,
      primary_address_line1: data.primary_address_line1,
      primary_address_line2: data.primary_address_line2 || null,
      primary_address_line3: data.primary_address_line3,
      primary_address_landmark: data.primary_address_landmark || null,
      primary_address_pincode: data.primary_address_pincode,
      primary_address_lat: data.primary_address_lat || null,
      primary_address_lng: data.primary_address_lng || null,
      address_category: data.addressCategory || null,
      address_type: data.addressType || null,
      assigned_to: data.assignedTo || null,
      lead_type: data.leadType,
      program_name: data.program_name || null,
      website: data.website ? (data.website.startsWith('http') ? data.website : `https://${data.website}`) : null,
      associated_sales_id: data.associatedSalesId || null,
      additional_contacts: additionalContacts.map(c => ({
        name: c.name,
        phone: c.phone,
        type: c.type,
        role: c.role,
        email: c.email || null,
        phone_type: c.phoneType,
        source: c.source || null,
      })), 
      additional_addresses: additionalAddresses.map(a => ({
        address_line1: a.address_line1,
        address_line2: a.address_line2 || null,
        address_line3: a.address_line3,
        address_landmark: a.address_landmark || null,
        address_pincode: a.address_pincode,
        address_lat: a.address_lat || null,
        address_lng: a.address_lng || null,
        type: a.type,
        category: a.category || null,
      })),
      notes: notes.map(n => ({
        author: n.author,
        text: n.text,
        category: n.category,
        sentiment: n.sentiment,
        priority: n.priority || null,
        status: n.status,
        next_meeting: n.nextMeeting || null,
        next_meeting_time: n.nextMeetingTime === "any" ? null : n.nextMeetingTime || null,
        contact_name: n.contactName || null,
        date: new Date().toISOString(),
      }))
    };
    
    try {
      if (isEditing && leadId) {
        await crmService.updateLead(leadId, fullData);
        toast({
          title: "Lead updated",
          description: `Lead ${data.tradeName} has been updated successfully.`,
          variant: "default",
        });
      } else {
        await crmService.createLead(fullData);
        toast({
          title: "Lead saved",
          description: `Lead details captured for ${data.tradeName}.`,
          variant: "default",
        });
        reset();
        setAdditionalContacts([]);
        setAdditionalAddresses([]);
        setNotes([]);
        setNewContact({ name: "", phone: "", type: "", role: "", email: "", phoneType: "", source: "" });
        setNewAddress({ address_line1: "", address_line2: "", address_line3: "", address_landmark: "", address_pincode: "", address_lat: "", address_lng: "", type: "", category: "" });
        setOtherSource("");
        setEditingContactId(null);
        setEditingAddressId(null);
        setCurrentStep(1);
      }
    } catch (error) {
      console.error("Error saving lead:", error);
      toast({
        title: "Error saving lead",
        description: "There was a problem saving the lead details.",
        variant: "destructive",
      });
    }
  };

  const nextStep = async () => {
    const fieldsToValidate: Record<number, (keyof LeadFormValues)[]> = {
      1: ["bapName", "tradeName", "registeredName", "website", "id"],
      2: ["contactName", "contactType", "role", "contactPhoneType", "contactPhone", "contactEmail", "contactLeadSource", "contactOtherLeadSource"],
      3: ["primary_address_line1", "primary_address_line2", "primary_address_line3", "primary_address_landmark", "primary_address_pincode", "primary_address_lat", "primary_address_lng", "addressCategory", "addressType"],
      4: [],
      5: ["status", "program_name", "leadType", "assignedTo", "associatedSalesId"]
    };

    const fields = fieldsToValidate[currentStep] || [];
    const isValid = fields.length > 0 ? await form.trigger(fields) : true;

    if (isValid && currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const isDiaryMode = displayMode === "diary-mode";

  return (
    <div className="w-full">
      <FullScreenLoader loading={isPageLoading} />
      <div className="mx-auto max-w-6xl rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 whitespace-nowrap">CRM: Add / Update Leads</h1>
            <p className="mt-1 text-sm text-gray-500">
              {isDiaryMode && <span className="font-semibold text-intellend-green mr-1">Step {currentStep} of {totalSteps}:</span>}
              Add or update lead records via the CRM interface.
            </p>
          </div>
          {!isDiaryMode && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => toggleAllSections(!allExpanded)}
              className="flex items-center gap-2 border-gray-300 text-xs h-9"
            >
              {allExpanded ? (
                <>
                  <Minimize2 className="h-3.5 w-3.5" /> Collapse All
                </>
              ) : (
                <>
                  <Maximize2 className="h-3.5 w-3.5" /> Expand All
                </>
              )}
            </Button>
          )}
          {isDiaryMode && (
            <div className="flex flex-wrap gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div 
                  key={i} 
                  className={`h-2 w-8 rounded-full transition-all ${i + 1 <= currentStep ? "bg-intellend-green" : "bg-gray-200"}`}
                />
              ))}
            </div>
          )}
        </div>

        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Section 1: Lead Identification */}
            <section className={`space-y-4 ${isDiaryMode && currentStep !== 1 ? "hidden" : ""}`}>
              <div 
                className={`flex items-center justify-between cursor-pointer group ${isDiaryMode ? "pointer-events-none" : ""}`}
                onClick={() => !isDiaryMode && toggleSection(1)}
              >
                <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-intellend-green/10 text-intellend-green text-sm">1</span>
                  Lead Identification
                </h3>
                {!isDiaryMode && (
                  <div className="text-gray-400 group-hover:text-intellend-green transition-colors">
                    {expandedSections[1] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </div>
                )}
              </div>
              
              <div className={!isDiaryMode && !expandedSections[1] ? "hidden" : "grid gap-4 sm:grid-cols-2"}>
                <FormField
                  control={control}
                  name="id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lead ID</FormLabel>
                      <FormControl><Input 
                          placeholder="Lead ID" 
                          {...field} 
                          disabled={true}
                          showSuccess={!!field.value && !errors.id}
                        /></FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="bapName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>BAP Name *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""} disabled={isDropdownOptionsLoading}>
                        <FormControl><SelectTrigger showSuccess={!!field.value && !errors.bapName}>
                            <SelectValue placeholder={isDropdownOptionsLoading ? "Loading BAPs..." : "Select BAP"} />
                          </SelectTrigger></FormControl>
                        <SelectContent>
                          {availableBaps.map(bap => (
                            <SelectItem key={bap.id} value={bap.name || bap.id.toString()}>{bap.name || "Unnamed BAP"}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="sm:col-span-2">
                  <FormField
                    control={control}
                    name="tradeName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Trade / Shop Name *</FormLabel>
                        <FormControl><Input 
                            placeholder="Shop or Brand name" 
                            {...field} 
                            showSuccess={!!field.value && !errors.tradeName}
                          /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="sm:col-span-2">
                  <FormField
                    control={control}
                    name="registeredName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Registered Business Name</FormLabel>
                        <FormControl><Input 
                            placeholder="Legal entity name" 
                            {...field} 
                            showSuccess={!!field.value && !errors.registeredName}
                          /></FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="sm:col-span-2">
                  <FormField
                    control={control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website</FormLabel>
                        <FormControl><Input 
                            placeholder="Website URL" 
                            {...field} 
                            showSuccess={!!field.value && !errors.website}
                          /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </section>
  
            {(!isDiaryMode || currentStep === 1) && <Separator className={isDiaryMode ? "hidden" : ""} />}
  
            {/* Section 2: Contact Information */}
            <section className={`space-y-4 ${isDiaryMode && currentStep !== 2 ? "hidden" : ""}`}>
              <div 
                className={`flex items-center justify-between cursor-pointer group ${isDiaryMode ? "pointer-events-none" : ""}`}
                onClick={() => !isDiaryMode && toggleSection(2)}
              >
                <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-intellend-green/10 text-intellend-green text-sm">2</span>
                  Contact Information
                </h3>
                {!isDiaryMode && (
                  <div className="text-gray-400 group-hover:text-intellend-green transition-colors">
                    {expandedSections[2] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </div>
                )}
              </div>
  
              <div className={!isDiaryMode && !expandedSections[2] ? "hidden" : "space-y-4"}>
                <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50 space-y-4">
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-900">Manage All Contacts ({additionalContacts.length + 1})</p>
                  <p className="text-xs text-gray-500">Add or view secondary/other contacts for this lead</p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setIsViewContactsOpen(true)}
                    className="flex items-center gap-2 border-gray-300 hover:bg-white text-xs h-9"
                  >
                    <Eye className="h-3.5 w-3.5" /> View All Contacts
                  </Button>
                  <Button 
                    type="button" 
                    size="sm"
                    onClick={() => {
                      setEditingContactId(null);
                      setNewContact({ name: "", phone: "", type: "", role: "", email: "", phoneType: "", source: "" });
                      setIsAddContactOpen(true);
                    }}
                    className="flex items-center gap-2 bg-intellend-green hover:bg-intellend-green/90 text-white text-xs h-9"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add New
                  </Button>
                </div>
              </div>
  
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <FormField
                    control={control}
                    name="contactName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name *</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter name" 
                            {...field} 
                            showSuccess={!!field.value && !errors.contactName}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="sm:col-span-2 grid grid-cols-2 gap-4">
                  <FormField
                    control={control}
                    name="contactType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type *</FormLabel>
                        <Select onValueChange={field.onChange} value="primary" disabled>
                          <FormControl>
                            <SelectTrigger showSuccess={true} className="bg-gray-50">
                              <SelectValue placeholder="Primary" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="primary">Primary</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Role *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || ""}>
                          <FormControl>
                            <SelectTrigger showSuccess={!!field.value && !errors.role}>
                              <SelectValue placeholder="Role" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Owner">Owner</SelectItem>
                            <SelectItem value="Manager">Manager</SelectItem>
                            <SelectItem value="Staff">Staff</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="sm:col-span-2 grid grid-cols-2 gap-4">
                  <FormField
                    control={control}
                    name="contactPhoneType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Type *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || ""}>
                          <FormControl>
                            <SelectTrigger showSuccess={!!field.value && !errors.contactPhoneType}>
                              <SelectValue placeholder="Phone Type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="mobile">Mobile</SelectItem>
                            <SelectItem value="landline">Landline</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="contactPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number *</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter phone" 
                            {...field} 
                            showSuccess={!!field.value && !errors.contactPhone}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={control}
                  name="contactEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email (Optional)</FormLabel>
                      <FormControl>
                        <Input 
                          type="email" 
                          placeholder="Enter email" 
                          {...field} 
                          showSuccess={!!field.value && !errors.contactEmail}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="contactLeadSource"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lead Source</FormLabel>
                      <div className="flex flex-col gap-2">
                        <Select onValueChange={field.onChange} value={field.value || ""} disabled={isDropdownOptionsLoading}>
                          <FormControl>
                            <SelectTrigger showSuccess={!!field.value && !errors.contactLeadSource}>
                              <SelectValue placeholder={isDropdownOptionsLoading ? "Loading sources..." : "Select Source"} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {availableLeadSources.map(source => (
                              <SelectItem key={source.id} value={source.name || source.id.toString()}>{source.name || "Unnamed Source"}</SelectItem>
                            ))}
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        {field.value === "Other" && (
                          <FormField
                            control={control}
                            name="contactOtherLeadSource"
                            render={({ field: otherField }) => (
                              <FormControl>
                                <Input 
                                  placeholder="Please specify other source" 
                                  {...otherField} 
                                  showSuccess={!!otherField.value && !errors.contactOtherLeadSource}
                                />
                              </FormControl>
                            )}
                          />
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              </div>
            </section>
  
            {(!isDiaryMode || currentStep === 2) && <Separator className={isDiaryMode ? "hidden" : ""} />}
  
            {/* Section 3: Location Details */}
            <section className={`space-y-4 ${isDiaryMode && currentStep !== 3 ? "hidden" : ""}`}>
              <div 
                className={`flex items-center justify-between cursor-pointer group ${isDiaryMode ? "pointer-events-none" : ""}`}
                onClick={() => !isDiaryMode && toggleSection(3)}
              >
                <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-intellend-green/10 text-intellend-green text-sm">3</span>
                  Location Details
                </h3>
                {!isDiaryMode && (
                  <div className="text-gray-400 group-hover:text-intellend-green transition-colors">
                    {expandedSections[3] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </div>
                )}
              </div>
  
              <div className={!isDiaryMode && !expandedSections[3] ? "hidden" : "space-y-4"}>
                <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50 space-y-4">
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-900">Manage All Addresses ({additionalAddresses.length + 1})</p>
                  <p className="text-xs text-gray-500">Add or view secondary/other addresses for this lead</p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setIsViewAddressesOpen(true)}
                    className="flex items-center gap-2 border-gray-300 hover:bg-white text-xs h-9"
                  >
                    <Eye className="h-3.5 w-3.5" /> View All Addresses
                  </Button>
                  <Button 
                    type="button" 
                    size="sm"
                    onClick={() => {
                      setEditingAddressId(null);
                      setNewAddress({ address_line1: "", address_line2: "", address_line3: "", address_landmark: "", address_pincode: "", type: "", category: "" });
                      setIsAddAddressOpen(true);
                    }}
                    className="flex items-center gap-2 bg-intellend-green hover:bg-intellend-green/90 text-white text-xs h-9"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add New
                  </Button>
                </div>
              </div>
  
              <div className="bg-slate-50 p-6 rounded-xl border border-gray-100 mb-6">
                <AddressFields 
                  form={form} 
                  prefix="primary" 
                  requiredFlags={[true, false, true, false, true]} 
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={control}
                  name="addressType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <Select onValueChange={field.onChange} value="primary" disabled>
                        <FormControl>
                          <SelectTrigger showSuccess={true} className="bg-gray-50">
                            <SelectValue placeholder="Primary" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="primary">Primary</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="addressCategory"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Category</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""} disabled={isDropdownOptionsLoading}>
                        <FormControl>
                          <SelectTrigger showSuccess={!!field.value && !errors.addressCategory}>
                            <SelectValue placeholder={isDropdownOptionsLoading ? "Loading categories..." : "Select Category"} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableAddressCategories.map(cat => (
                            <SelectItem key={cat.id} value={cat.name || cat.id.toString()}>{cat.name || "Unnamed Category"}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>
              </div>
            </section>
  
            {(!isDiaryMode || currentStep === 3) && <Separator className={isDiaryMode ? "hidden" : ""} />}

            {/* Section 4: Follow-up & Notes */}
            <section className={`space-y-4 ${isDiaryMode && currentStep !== 4 ? "hidden" : ""}`}>
              <div 
                className={`flex items-center justify-between cursor-pointer group ${isDiaryMode ? "pointer-events-none" : ""}`}
                onClick={() => !isDiaryMode && toggleSection(4)}
              >
                <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-intellend-green/10 text-intellend-green text-sm">4</span>
                  Follow-up & Notes
                </h3>
                {!isDiaryMode && (
                  <div className="text-gray-400 group-hover:text-intellend-green transition-colors">
                    {expandedSections[4] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </div>
                )}
              </div>
              
              <div className={!isDiaryMode && !expandedSections[4] ? "hidden" : "space-y-4"}>
                <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50 space-y-4">
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-900">Interaction History ({notes.length})</p>
                  <p className="text-xs text-gray-500">Record calls, emails, and meetings or view past notes</p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setIsViewNotesOpen(true)}
                    className="flex items-center gap-2 border-gray-300 hover:bg-white text-xs h-9"
                  >
                    <History className="h-3.5 w-3.5" /> View History
                  </Button>
                  <Button 
                    type="button" 
                    size="sm"
                    onClick={() => setIsAddNoteOpen(true)}
                    className="flex items-center gap-2 bg-intellend-green hover:bg-intellend-green/90 text-white text-xs h-9"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add Note
                  </Button>
                </div>
              </div>

              {notes.length > 0 && (
                <div className="space-y-3 mt-6">
                  <div className="flex items-center justify-between px-1">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Recent Interactions</p>
                    {notes.length > 3 && (
                      <button 
                        type="button" 
                        onClick={() => setIsViewNotesOpen(true)}
                        className="text-xs text-intellend-green font-medium hover:underline"
                      >
                        View All
                      </button>
                    )}
                  </div>
                  <div className="grid gap-3">
                    {notes.slice(0, 3).map((note) => (
                      <div key={note.id} className="p-4 rounded-xl border border-gray-100 bg-white shadow-sm flex flex-col gap-2 hover:border-intellend-green/20 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-gray-900">{note.author}</span>
                            {note.contactName && (
                              <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-100 flex items-center gap-1">
                                <User className="h-2.5 w-2.5" /> With: {note.contactName}
                              </span>
                            )}
                            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${
                            note.category === "call" ? "bg-blue-50 text-blue-600 border-blue-100" :
                            note.category === "email" ? "bg-purple-50 text-purple-600 border-purple-100" :
                            note.category === "meeting" ? "bg-orange-50 text-orange-600 border-orange-100" :
                            "bg-gray-50 text-gray-600 border-gray-100"
                          }`}>
                            {note.category}
                          </span>
                          {note.sentiment === "positive" && <Smile className="h-3.5 w-3.5 text-green-500" />}
                          {note.sentiment === "negative" && <Frown className="h-3.5 w-3.5 text-red-500" />}
                        </div>
                        <span className="text-xs text-gray-400 font-medium">{note.date}</span>
                      </div>
                    </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </section>

            {(!isDiaryMode || currentStep === 4) && <Separator className={isDiaryMode ? "hidden" : ""} />}

            {/* Section 5: Status & Assignment */}
            <section className={`space-y-4 ${isDiaryMode && currentStep !== 5 ? "hidden" : ""}`}>
              <div 
                className={`flex items-center justify-between cursor-pointer group ${isDiaryMode ? "pointer-events-none" : ""}`}
                onClick={() => !isDiaryMode && toggleSection(5)}
              >
                <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-intellend-green/10 text-intellend-green text-sm">5</span>
                  Status & Assignment
                </h3>
                {!isDiaryMode && (
                  <div className="text-gray-400 group-hover:text-intellend-green transition-colors">
                    {expandedSections[5] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </div>
                )}
              </div>
              
              <div className={!isDiaryMode && !expandedSections[5] ? "hidden" : "grid gap-4 sm:grid-cols-2"}>
                <FormField
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""} disabled={isDropdownOptionsLoading}>
                        <FormControl>
                          <SelectTrigger showSuccess={!!field.value && !errors.status}>
                            <SelectValue placeholder={isDropdownOptionsLoading ? "Loading statuses..." : "Select Status"} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableStatuses.map(status => (
                            <SelectItem key={status.id} value={status.name || status.id.toString()}>{status.name || "Unnamed Status"}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="program_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Program</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""} disabled={isDropdownOptionsLoading}>
                        <FormControl>
                          <SelectTrigger showSuccess={!!field.value && !errors.program_name}>
                            <SelectValue placeholder={isDropdownOptionsLoading ? "Loading programs..." : "Select Program"} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availablePrograms.map(program => (
                            <SelectItem key={program.id} value={program.name || program.id.toString()}>{program.name || "Unnamed Program"}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="leadType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lead Type *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger showSuccess={!!field.value && !errors.leadType}>
                            <SelectValue placeholder="Select Lead Type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="inbound">Inbound (Customer Called)</SelectItem>
                          <SelectItem value="outbound">Outbound (Sales Called)</SelectItem>
                          <SelectItem value="referral">Referral</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="assignedTo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Assigned To</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || ""}
                        disabled={isDropdownOptionsLoading}
                      >
                        <FormControl>
                          <SelectTrigger showSuccess={!!field.value && !errors.assignedTo}>
                            <SelectValue placeholder={isDropdownOptionsLoading ? "Loading users..." : "Select assignee"} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableUsers.map((user) => (
                              <SelectItem key={user.id} value={String(user.id)}>
                                ({user.id}) {user.username || "unknown"}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="associatedSalesId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Associated Sales Id</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Sales Id" 
                          {...field} 
                          showSuccess={!!field.value && !errors.associatedSalesId}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </section>

          <div className="flex flex-wrap items-center justify-between pt-6 border-t border-gray-100 gap-4">
            {isDiaryMode ? (
              <>
                <Button 
                  variant="outline" 
                  type="button" 
                  onClick={prevStep} 
                  disabled={currentStep === 1}
                  className="px-8"
                >
                  Previous Step
                </Button>
                
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" type="button" onClick={() => reset()} className="px-6">Reset</Button>
                  {currentStep === totalSteps ? (
                    <Button 
                      key="submit-btn"
                      type="submit" 
                      disabled={isSubmitting} 
                      className="px-8 bg-intellend-green hover:bg-intellend-green/90 text-white"
                    >
                      {isSubmitting ? "Saving..." : "Finalize & Save"}
                    </Button>
                  ) : (
                    <Button 
                      key="next-btn"
                      type="button" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        nextStep();
                      }} 
                      className="px-8 bg-intellend-green hover:bg-intellend-green/90 text-white"
                    >
                      Next Step
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div className="flex flex-wrap items-center justify-end w-full gap-3">
                <Button variant="outline" type="button" onClick={() => reset()} className="px-8">Reset Form</Button>
                <Button type="submit" disabled={isSubmitting} className="px-8 bg-intellend-green hover:bg-intellend-green/90 text-white">
                  {isSubmitting ? "Saving..." : "Save Lead Details"}
                </Button>
              </div>
            )}
          </div>
        </form>
      </Form>
    </div>

    {/* Add Contact Dialog */}
      <Dialog open={isAddContactOpen} onOpenChange={(open) => {
        setIsAddContactOpen(open);
        if (!open) {
          setEditingContactId(null);
          setNewContact({ name: "", phone: "", type: "", role: "", email: "", phoneType: "", source: "" });
        }
      }}>
        <DialogContent className="sm:max-w-[425px] max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-2 border-b">
            <DialogTitle>{editingContactId ? "Edit Contact" : "Add Additional Contact"}</DialogTitle>
            <DialogDescription>
              {editingContactId ? "Update the details for this contact." : "Enter details for a secondary or other contact."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Full Name *</label>
              <Input 
                value={newContact.name} 
                onChange={(e) => setNewContact({...newContact, name: e.target.value})} 
                placeholder="Enter name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label className="text-sm font-medium">Type</label>
                <Select 
                  value={editingContactId === "primary" ? "primary" : newContact.type} 
                  onValueChange={(val) => setNewContact({...newContact, type: val as "secondary" | "other"})}
                  disabled={editingContactId === "primary"}
                >
                  <SelectTrigger showSuccess={!!newContact.type}>
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {editingContactId === "primary" && <SelectItem value="primary">Primary</SelectItem>}
                    <SelectItem value="secondary">Secondary</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium">Role</label>
                <Select 
                  value={newContact.role} 
                  onValueChange={(val) => setNewContact({...newContact, role: val})}
                >
                  <SelectTrigger showSuccess={!!newContact.role}>
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Owner">Owner</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label className="text-sm font-medium">Phone Type</label>
                <Select 
                  value={newContact.phoneType} 
                  onValueChange={(val) => setNewContact({...newContact, phoneType: val as "mobile" | "landline"})}
                >
                  <SelectTrigger showSuccess={!!newContact.phoneType}>
                    <SelectValue placeholder="Phone Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mobile">Mobile</SelectItem>
                    <SelectItem value="landline">Landline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium">Phone Number *</label>
                <Input 
                  value={newContact.phone} 
                  onChange={(e) => setNewContact({...newContact, phone: e.target.value})} 
                  placeholder="Enter phone"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Email (Optional)</label>
              <Input 
                type="email"
                value={newContact.email} 
                onChange={(e) => setNewContact({...newContact, email: e.target.value})} 
                placeholder="Enter email"
              />
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Lead Source</label>
              <div className="flex flex-col gap-2">
                <Select 
                  value={newContact.source} 
                  onValueChange={(val) => setNewContact({...newContact, source: val})}
                  disabled={isDropdownOptionsLoading}
                >
                  <SelectTrigger showSuccess={!!newContact.source}>
                    <SelectValue placeholder={isDropdownOptionsLoading ? "Loading sources..." : "Select Source"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLeadSources.map(source => (
                      <SelectItem key={source.id} value={source.name || source.id.toString()}>{source.name || "Unnamed Source"}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {newContact.source === "Other" && (
                  <Input 
                    value={otherSource} 
                    onChange={(e) => setOtherSource(e.target.value)} 
                    placeholder="Please specify other source"
                  />
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="p-6 pt-2 border-t">
            <Button variant="outline" onClick={() => setIsAddContactOpen(false)}>Cancel</Button>
            <Button className="bg-intellend-green hover:bg-intellend-green/90 text-white" onClick={handleAddContact}>
              {editingContactId ? "Update Contact" : "Add Contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Contacts Dialog */}
      <Dialog open={isViewContactsOpen} onOpenChange={setIsViewContactsOpen}>
        <DialogContent className="sm:max-w-[600px] h-[70vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b">
            <div className="flex flex-col gap-4 items-start">
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-intellend-green" />
                All Lead Contacts ({additionalContacts.length + 1})
              </DialogTitle>
              <Button 
                type="button" 
                size="sm"
                onClick={() => {
                  setEditingContactId(null);
                  setNewContact({ name: "", phone: "", type: "", role: "", email: "", phoneType: "", source: "" });
                  setIsAddContactOpen(true);
                  setIsViewContactsOpen(false);
                }}
                className="bg-intellend-green hover:bg-intellend-green/90 text-white flex items-center gap-1"
              >
                <Plus className="h-4 w-4" /> Add Contact
              </Button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Primary Contact (from Form State) */}
            <div 
              className="flex items-center justify-between p-4 rounded-lg border-2 border-amber-200 bg-amber-50/30 hover:bg-amber-50/50 cursor-pointer transition-colors shadow-sm"
              onClick={() => {
                const primaryData: Partial<AdditionalContact> = {
                  name: getValues("contactName"),
                  role: getValues("role"),
                  phone: getValues("contactPhone"),
                  email: getValues("email"),
                  phoneType: getValues("mobile") ? "mobile" : "landline",
                  source: "Existing"
                };
                setNewContact(primaryData);
                setEditingContactId("primary");
                setIsAddContactOpen(true);
                setIsViewContactsOpen(false);
              }}
            >
              <div className="flex-1 flex flex-col items-start">
                <h4 className="font-semibold text-gray-900">{getValues("contactName") || "Unnamed Primary"}</h4>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded border mr-2">
                    Primary
                  </span>
                  - {getValues("role")}
                </p>
                <p className="text-sm text-gray-500 mt-0.5">{getValues("contactPhoneType") || "mobile"}: {getValues("contactPhone")}</p>
                <p className="text-xs text-gray-400 mt-0.5">{getValues("contactEmail") || "No email provided"}</p>
                <p className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded border border-gray-200 inline-block mt-1">
                  Source: {getValues("contactLeadSource") || "Not specified"}
                </p>
              </div>
              <div className="flex gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <Star className="h-4 w-4 fill-amber-600" />
                </div>
              </div>
            </div>

            {/* Additional Contacts */}
            {additionalContacts.map((contact) => (
              <div 
                key={contact.id} 
                className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer transition-colors shadow-sm"
                onClick={() => {
                  setNewContact(contact);
                  setEditingContactId(contact.id);
                  setIsAddContactOpen(true);
                  setIsViewContactsOpen(false);
                }}
              >
                <div className="flex-1 flex flex-col items-start">
                  <h4 className="font-semibold text-gray-900">{contact.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border mr-2 ${
                      contact.type === "secondary" 
                        ? "bg-blue-100 text-blue-700 border-blue-200" 
                        : "bg-slate-900 text-white border-slate-900"
                    }`}>
                      {contact.type}
                    </span>
                    - {contact.role}
                  </p>
                  <p className="text-sm text-gray-500 mt-0.5">{contact.phoneType}: {contact.phone}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{contact.email || "No email provided"}</p>
                  <p className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded border border-gray-200 inline-block mt-1">
                    Source: {contact.source || "Not specified"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-intellend-green hover:text-intellend-green hover:bg-intellend-green/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      initiateMakePrimary(contact);
                    }}
                    title="Make Primary"
                  >
                    <Star className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveContact(contact.id);
                    }}
                    title="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Make Primary Confirmation Dialog */}
      <Dialog open={isSwapConfirmOpen} onOpenChange={setIsSwapConfirmOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <ArrowRightLeft className="h-5 w-5" />
              Change Primary Contact
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to make <strong>{contactToMakePrimary?.name}</strong> the primary contact?
            </DialogDescription>
          </DialogHeader>
          <div className="py-6 space-y-4">
            <p className="text-sm text-gray-600">
              One lead can have only one primary contact. Please choose what to mark the current primary lead as:
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div 
                className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  downgradeType === "secondary" ? "border-intellend-green bg-intellend-green/5 ring-1 ring-intellend-green" : "border-gray-200 bg-white hover:border-gray-300"
                }`}
                onClick={() => setDowngradeType("secondary")}
              >
                <div>
                  <p className="font-bold text-gray-900">Secondary</p>
                  <p className="text-xs text-gray-500">Marks as secondary contact</p>
                </div>
                {downgradeType === "secondary" && <div className="h-4 w-4 rounded-full bg-intellend-green" />}
              </div>
              <div 
                className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  downgradeType === "other" ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-gray-200 bg-white hover:border-gray-300"
                }`}
                onClick={() => setDowngradeType("other")}
              >
                <div>
                  <p className="font-bold text-gray-900">Other</p>
                  <p className="text-xs text-gray-500 text-slate-500">Marks as other contact</p>
                </div>
                {downgradeType === "other" && <div className="h-4 w-4 rounded-full bg-slate-900" />}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsSwapConfirmOpen(false)}>Cancel</Button>
            <Button 
              className="bg-intellend-green hover:bg-intellend-green/90 text-white" 
              onClick={confirmSwapPrimary}
            >
              Yes, Make Primary
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Notes History Dialog */}
      <Dialog open={isViewNotesOpen} onOpenChange={setIsViewNotesOpen}>
        <DialogContent className="sm:max-w-[700px] h-[80vh] flex flex-col p-0 overflow-hidden gap-0">
          <DialogHeader className="p-6 pb-4 border-b">
            <div className="flex flex-col gap-4 items-start">
              <DialogTitle className="flex items-center gap-2">
                <History className="h-5 w-5 text-intellend-green" />
                Note History ({notes.length})
              </DialogTitle>
              <Button 
                type="button" 
                size="sm"
                onClick={() => {
                  setIsViewNotesOpen(false);
                  setIsAddNoteOpen(true);
                }}
                className="bg-intellend-green hover:bg-intellend-green/90 text-white flex items-center gap-1"
              >
                <Plus className="h-4 w-4" /> Add Note
              </Button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6">
            {notes.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No previous notes found.</p>
            ) : (
              <div className="relative border-l-2 border-gray-100 ml-3 pl-6 space-y-6">
                {notes.map((note, index) => (
                  <div key={note.id} className="relative">
                    <div className={`absolute -left-[31px] top-1.5 h-4 w-4 rounded-full border-2 border-white ${index === 0 ? "bg-intellend-green scale-110 shadow-sm ring-2 ring-intellend-green/20" : "bg-gray-300"}`} />
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-900">{note.author}</span>
                            {note.contactName && (
                              <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-100 flex items-center gap-1">
                                <User className="h-2.5 w-2.5" /> With: {note.contactName}
                              </span>
                            )}
                            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${
                            note.category === "call" ? "bg-blue-50 text-blue-600 border-blue-100" :
                            note.category === "email" ? "bg-purple-50 text-purple-600 border-purple-100" :
                            note.category === "meeting" ? "bg-orange-50 text-orange-600 border-orange-100" :
                            "bg-gray-50 text-gray-600 border-gray-100"
                          }`}>
                            {note.category}
                          </span>
                          {note.sentiment === "positive" && <Smile className="h-3.5 w-3.5 text-green-500" />}
                          {note.sentiment === "negative" && <Frown className="h-3.5 w-3.5 text-red-500" />}
                        </div>
                        <span className="text-xs text-gray-400 font-medium">{note.date}</span>
                      </div>
                      
                      <div className={`p-4 rounded-xl border transition-all ${index === 0 ? "text-gray-900 bg-intellend-green/5 border-intellend-green/20 shadow-sm" : "text-gray-600 bg-gray-50 border-gray-100"}`}>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{note.text}</p>
                        
                        {(note.nextMeeting || note.priority) && (
                          <div className="mt-3 pt-3 border-t border-gray-200/50 flex flex-wrap gap-3">
                            {note.nextMeeting && (
                              <div className="flex items-center gap-1.5 text-[11px] font-medium text-intellend-green bg-white px-2 py-1 rounded border border-intellend-green/10 shadow-sm">
                                <Clock className="h-3 w-3" />
                                Follow-up: {note.nextMeeting} {note.nextMeetingTime && `at ${note.nextMeetingTime}`}
                              </div>
                            )}
                            {note.priority && (
                              <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase px-2 py-1 rounded border shadow-sm ${
                                note.priority === "urgent" || note.priority === "high" ? "bg-red-50 text-red-600 border-red-100" : "bg-blue-50 text-blue-600 border-blue-100"
                              }`}>
                                <AlertCircle className="h-3 w-3" />
                                {note.priority}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Add Note Dialog (shared AddNoteDialog, local mode) ── */}
      <AddNoteDialog
        open={isAddNoteOpen}
        category={addNoteCategory}
        authorName={currentUser?.username ?? "Agent"}
        contacts={[
          ...(getValues("contactName")
            ? [{ id: "primary", name: getValues("contactName"), type: "primary" }]
            : []),
          ...additionalContacts.map((c) => ({ id: c.id, name: c.name, type: c.type })),
        ]}
        onClose={() => setIsAddNoteOpen(false)}
        onSave={handleAddNote}
      />

      {/* Add Address Dialog */}
      <Dialog open={isAddAddressOpen} onOpenChange={(open) => {
        setIsAddAddressOpen(open);
        if (!open) {
          setEditingAddressId(null);
          setNewAddress({ address_line1: "", address_line2: "", address_line3: "", address_landmark: "", address_pincode: "", type: "", category: "" });
        }
      }}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-2 border-b">
            <DialogTitle>{editingAddressId ? "Edit Address" : "Add Additional Address"}</DialogTitle>
            <DialogDescription>
              {editingAddressId ? "Update the details for this location." : "Enter details for an additional business location."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <AddressFields 
              prefix="temp" 
              form={{
                setValue: (name: string, value: string, options?: any) => {
                  const field = name.replace("temp_address_", "");
                  setNewAddress(prev => ({
                    ...prev,
                    [`address_${field}`]: value
                  }));
                },
                getValues: (name: string) => {
                  const field = name.replace("temp_address_", "");
                  return (newAddress as any)[`address_${field}`];
                },
                watch: (name: string) => {
                  const field = name.replace("temp_address_", "");
                  return (newAddress as any)[`address_${field}`];
                },
                formState: { errors: {} }
              }}
            />
            <div className="grid gap-2">
              <label className="text-sm font-medium">Type</label>
              <Select 
                value={editingAddressId === "primary" ? "primary" : newAddress.type} 
                onValueChange={(val) => setNewAddress({...newAddress, type: val as any})}
                disabled={editingAddressId === "primary"}
              >
                <SelectTrigger showSuccess={!!(editingAddressId === "primary" ? "primary" : newAddress.type)}>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {editingAddressId === "primary" && <SelectItem value="primary">Primary</SelectItem>}
                  <SelectItem value="secondary">Secondary</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Category</label>
              <Select 
                value={newAddress.category} 
                onValueChange={(val) => setNewAddress({...newAddress, category: val})}
              >
                <SelectTrigger showSuccess={!!newAddress.category}>
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  {availableAddressCategories.map(cat => (
                    <SelectItem key={cat.id} value={cat.name || cat.id.toString()}>{cat.name || "Unnamed Category"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="p-6 pt-2 border-t">
            <Button variant="outline" onClick={() => setIsAddAddressOpen(false)}>Cancel</Button>
            <Button className="bg-intellend-green hover:bg-intellend-green/90 text-white" onClick={handleAddAddress}>
              {editingAddressId ? "Update Address" : "Add Address"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Addresses Dialog */}
      <Dialog open={isViewAddressesOpen} onOpenChange={setIsViewAddressesOpen}>
        <DialogContent className="sm:max-w-[600px] h-[70vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b">
            <div className="flex flex-col gap-4 items-start">
              <DialogTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-intellend-green" />
                All Lead Addresses ({additionalAddresses.length + 1})
              </DialogTitle>
              <Button 
                type="button" 
                size="sm"
                onClick={() => {
                  setEditingAddressId(null);
                  setNewAddress({ address_line1: "", address_line2: "", address_line3: "", address_landmark: "", address_pincode: "", type: "", category: "" });
                  setIsAddAddressOpen(true);
                  setIsViewAddressesOpen(false);
                }}
                className="bg-intellend-green hover:bg-intellend-green/90 text-white flex items-center gap-1"
              >
                <Plus className="h-4 w-4" /> Add Address
              </Button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Primary Address (from Form State) */}
            <div 
              className="flex items-center justify-between p-4 rounded-lg border-2 border-amber-200 bg-amber-50/30 hover:bg-amber-50/50 cursor-pointer transition-colors shadow-sm"
              onClick={() => {
                const primaryData: Partial<AdditionalAddress> = {
                  address_line1: getValues("primary_address_line1"),
                  address_line2: getValues("primary_address_line2"),
                  address_pincode: getValues("primary_address_pincode"),
                  address_landmark: getValues("primary_address_landmark"),
                  address_line3: getValues("primary_address_line3"),
                  address_lat: getValues("primary_address_lat"),
                  address_lng: getValues("primary_address_lng"),
                  category: getValues("addressCategory") || "office",
                  type: "primary"
                };
                setNewAddress(primaryData);
                setEditingAddressId("primary");
                setIsAddAddressOpen(true);
                setIsViewAddressesOpen(false);
              }}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-gray-900 line-clamp-1">
                    {getValues("primary_address_line1") ? `${getValues("primary_address_line1")}, ${getValues("primary_address_line2")}` : "Unnamed Primary Address"}
                  </h4>
                  <div className="flex gap-1">
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">
                      Primary
                    </span>
                    {getValues("addressCategory") && (
                      <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                        {getValues("addressCategory")}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-500">{getValues("primary_address_line3")} - {getValues("primary_address_pincode")}</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {getValues("primary_address_landmark") && <p className="text-xs text-gray-400">Landmark: {getValues("primary_address_landmark")}</p>}
                  {getValues("primary_address_lat") && (
                    <span className="text-[10px] text-gray-400">Coords: {getValues("primary_address_lat")}, {getValues("primary_address_lng")}</span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <Star className="h-4 w-4 fill-amber-600" />
                </div>
              </div>
            </div>

            {/* Additional Addresses */}
            {additionalAddresses.map((address) => (
              <div 
                key={address.id} 
                className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer transition-colors shadow-sm"
                onClick={() => {
                  setNewAddress(address);
                  setEditingAddressId(address.id);
                  setIsAddAddressOpen(true);
                  setIsViewAddressesOpen(false);
                }}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900 line-clamp-1">{address.address_line1}, {address.address_line2}</h4>
                    <div className="flex gap-1">
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${
                        address.type === "secondary" 
                          ? "bg-blue-100 text-blue-700 border-blue-200" 
                          : "bg-slate-900 text-white border-slate-900"
                      }`}>
                        {address.type}
                      </span>
                      <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                        {address.category}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">{address.address_line3} - {address.address_pincode}</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {address.address_landmark && <p className="text-xs text-gray-400">Landmark: {address.address_landmark}</p>}
                    {address.address_lat && (
                      <span className="text-[10px] text-gray-400">Coords: {address.address_lat}, {address.address_lng}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-intellend-green hover:text-intellend-green hover:bg-intellend-green/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      initiateMakePrimaryAddress(address);
                    }}
                    title="Make Primary"
                  >
                    <Star className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveAddress(address.id);
                    }}
                    title="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Make Primary Address Confirmation Dialog */}
      <Dialog open={isAddressSwapConfirmOpen} onOpenChange={setIsAddressSwapConfirmOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <ArrowRightLeft className="h-5 w-5" />
              Change Primary Address
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to make this address the primary location?
            </DialogDescription>
          </DialogHeader>
          <div className="py-6 space-y-4">
            {addressToMakePrimary && (
              <div className="p-3 rounded-md bg-gray-50 border border-gray-100 mb-4">
                <p className="text-sm font-semibold">{addressToMakePrimary.address_line1}, {addressToMakePrimary.address_line2}</p>
                <p className="text-xs text-gray-500">{addressToMakePrimary.address_line3}, {addressToMakePrimary.address_pincode}</p>
              </div>
            )}
            <p className="text-sm text-gray-600">
              Please choose what to mark the current primary address as:
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div 
                className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  addressDowngradeType === "secondary" ? "border-intellend-green bg-intellend-green/5 ring-1 ring-intellend-green" : "border-gray-200 bg-white hover:border-gray-300"
                }`}
                onClick={() => setAddressDowngradeType("secondary")}
              >
                <div>
                  <p className="font-bold text-gray-900">Secondary</p>
                </div>
                {addressDowngradeType === "secondary" && <div className="h-4 w-4 rounded-full bg-intellend-green" />}
              </div>
              <div 
                className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  addressDowngradeType === "other" ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-gray-200 bg-white hover:border-gray-300"
                }`}
                onClick={() => setAddressDowngradeType("other")}
              >
                <div>
                  <p className="font-bold text-gray-900">Other</p>
                </div>
                {addressDowngradeType === "other" && <div className="h-4 w-4 rounded-full bg-slate-900" />}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsAddressSwapConfirmOpen(false)}>Cancel</Button>
            <Button 
              className="bg-intellend-green hover:bg-intellend-green/90 text-white" 
              onClick={confirmAddressSwap}
            >
              Yes, Make Primary
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Address Confirmation Dialog */}
      <Dialog open={isAddressDeleteConfirmOpen} onOpenChange={setIsAddressDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Confirm Address Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this address?
            </DialogDescription>
          </DialogHeader>
          {addressToDelete && (
            <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
              <p className="text-sm font-semibold text-gray-900">{addressToDelete.address_line1}</p>
              <p className="text-xs text-gray-500">{addressToDelete.type} • {addressToDelete.address_line3}</p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button variant="outline" onClick={() => setIsAddressDeleteConfirmOpen(false)}>Cancel</Button>
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white" 
              onClick={confirmDeleteAddress}
            >
              Delete Address
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Contact Confirmation Dialog */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this contact? This action will be recorded in the lead history.
            </DialogDescription>
          </DialogHeader>
          {contactToDelete && (
            <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
              <p className="text-sm font-semibold text-gray-900">{contactToDelete.name}</p>
              <p className="text-xs text-gray-500">{contactToDelete.role} • {contactToDelete.phone}</p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>Cancel</Button>
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white" 
              onClick={confirmDeleteContact}
            >
              Confirm Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMLeadForm;

